import { timeZones } from '@constants/index';
import { format } from 'date-fns-tz';
import isSameSecond from 'date-fns/isSameSecond';
import parseISO from 'date-fns/parseISO';
import Cookies from 'js-cookie';
import qs from 'qs';
import { DateTime } from 'luxon';
import getLocalTimezoneAbbreviation from './getLocalTimezoneAbbreviation';

export function capitalize(s: string) {
  return s[0].toUpperCase() + s.substring(1).toLowerCase();
}

export function formatLocalTimezoneSelect() {
  let offset = DateTime.now().toFormat('ZZ');
  if (offset[1] === '0') offset = offset[0] + offset.slice(2);
  const localAbbreviation = getLocalTimezoneAbbreviation();
  return `(GMT ${offset})${localAbbreviation.includes('GMT') ? '' : ' ' + localAbbreviation}`;
}

export function setToStateAndCookie(setFn: Function, cookieName: string, value: string) {
  if (!setFn || !cookieName || !value)
    return console.error(
      'Missing required parameter in setToStateAndCookieFn',
      `setFn: ${setFn}`,
      `cookieName: ${cookieName}`,
      `value: ${value}`
    );
  setFn(value);
  Cookies.set(cookieName, value);
}

export function caseChange(s, initialCase = 'kebab', targetCase = 'camel') {
  if (!s) return;
  let workArray;

  switch (initialCase) {
    case 'kebab':
      workArray = s.split('-');
      break;

    case 'snake':
      workArray = s.split('_');

    case 'camel':
      workArray = s.split(/(?=[A-Z])/).map(s => s.toLowerCase());
      break;
  }

  switch (targetCase) {
    case 'kebab':
      return workArray.join('-');

    case 'snake':
      return workArray.join('_');

    case 'camel':
      const capitalizedWorkArray = workArray.map((word, index) => {
        if (index === 0) return word;
        return word.slice(0, 1).toLocaleUpperCase() + word.slice(1);
      });
      return capitalizedWorkArray.join('');
  }
}

export function truncateString(s: string, maxCharLength = 50) {
  if (!s) return null;
  if (maxCharLength > s.length) return s;

  return s.substring(0, maxCharLength - 3).trim() + '...';
}

export function buildQueryString(endpoint: string, queryParams = {}) {
  const params = Object.entries(queryParams);
  const paramsNumber = params.length;
  if (!paramsNumber) {
    const error = 'No query params provided to buildQueryString fn.';
    console.error(error);
    throw {
      error,
    };
  }

  return `${endpoint.substring(0)}?${qs.stringify(queryParams)}`;
}

export function buildAdonisQueryString(endpoint: string, queryParams: string[] = []) {
  if (!queryParams.length) {
    const error = 'No query params provided to buildAdonisQueryString fn.';
    console.error(error); // eslint-disable-line no-console
    throw { error };
  }
  const endpointCharLength = endpoint.length;

  let finalEndpoint;
  if (endpoint[endpointCharLength - 1] === '/') {
    finalEndpoint = endpoint.substring(0, endpointCharLength - 1);
  } else finalEndpoint = endpoint;

  return [[finalEndpoint], ...queryParams].join('/');
}

export function getShortTimezone(timezone) {
  return timeZones.filter(tz => tz.value === timezone)[0].label.split(' ')[2];
}

export const shuffleArray = array => {
  const rArray = array.slice();

  for (let i = rArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    // ES6 destructure-style swap
    [rArray[i], rArray[j]] = [rArray[j], rArray[i]];
  }

  return rArray;
};

export function stripHtml(html) {
  if (typeof document !== undefined) {
    // Create a new div element
    var temporalDivElement = document.createElement('div');
    // Set the HTML content with the providen
    temporalDivElement.innerHTML = html;
    // Retrieve the text property of the element (cross-browser support)
    return temporalDivElement.textContent || temporalDivElement.innerText || '';
  }
}

export function formatDisplayTime(
  timestamp: string,
  options: {
    noYear?: boolean;
    upperAmPm?: boolean;
    dayYearComma?: boolean;
    convertUTCtoLocal?: boolean;
    customDate?: string;
  } = {
    noYear: false,
    upperAmPm: false,
    dayYearComma: false,
    convertUTCtoLocal: false,
    customDate: '',
  }
) {
  const date = new Date(Date.parse(options.convertUTCtoLocal ? timestamp + '+00:00' : timestamp));
  let dateFormatString = options.customDate;
  if (!options.customDate) {
    dateFormatString = 'd LLL';
    if (options.dayYearComma) dateFormatString += ',';
    if (!options.noYear) dateFormatString += ' yyyy';
  }
  return {
    date: format(date, dateFormatString),
    hour: options?.upperAmPm ? format(date, 'hh:mm a') : format(date, 'hh:mm a').toLowerCase(),
    timezone: getLocalTimezoneAbbreviation(),
  };
}

export function formatKebabDateColonTime(time) {
  const date = new Date(parseISO(time));
  return format(date, 'yyyy-MM-d hh:mm:ss a');
}

export function formatSlashDate(timestamp) {
  let formattedTimestamp = typeof timestamp === 'string' ? timestamp.replace(' ', 'T') : timestamp;

  const date = new Date(formattedTimestamp);

  return {
    shortDate: format(date, 'd/LLL'),
    fullDate: format(date, 'd/MM/yyyy'),
  };
}

export function isEdited({ created_at, updated_at }: IsEditedProps) {
  return !isSameSecond(new Date(created_at), new Date(updated_at));
}

type IsEditedProps = {
  created_at: Date | string;
  updated_at: Date | string;
};

export function makeHtmlStyleString(styleObject: object) {
  return Object.entries(styleObject)
    .map(([style, value]) => {
      const css = caseChange(style, 'camel', 'kebab');
      const cssValue = typeof value === 'string' && value.includes(' ') ? `"${value}"` : value;
      return `${css}: ${cssValue};`;
    })
    .join(' ');
}

export function encodeShortUrl({ slug, code }: Url) {
  return slug + '--' + code;
}

export function isRoleAuthorized(roleId: number, authorizedRoles: [string | number]) {
  return authorizedRoles.some(role => roleId == role);
}

export function isRoleNotAuthorized(roleId: number, notAuthorizedRoles: [string | number]) {
  return notAuthorizedRoles.some(role => roleId == role);
}
